 import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useAuth } from "../../../auth/useAuth";
import {
  DEVICE_TYPES_API,
  deleteRequest,
  getErrorMessage,
  getRequest,
  INVENTORY_API,
  MANUFACTURING_API,
  patchRequest,
  postRequest,
} from "./api";
import {
  EmptyStateCard,
  PageShell,
  SectionCard,
  panelSx,
  statusChip,
  metricTileSx,
  controlDeskSx,
} from "./ManufacturingCommon";

const emptyForm = {
  name: "",
  code: "",
  inventory_asset_type_id: "",
  device_type_id: "",
  manufacturer_name: "CoreData",
  model_name: "",
  hardware_revision: "A1",
  bom_revision: "",
  pcb_revision: "",
  enclosure_revision: "",
  default_base_os_version: "",
  default_test_plan_version: "",
  is_active: true,
  notes: "",
};

function normalizeCollection(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
}

function ManufacturingProductsSkeleton() {
  const cardSkeletons = Array.from({ length: 4 });
  const topFields = Array.from({ length: 4 });
  const lowerFields = Array.from({ length: 3 });

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {cardSkeletons.map((_, index) => (
        <Box sx={{ width: "100%" }} key={index}>
          <Box sx={{ ...panelSx, overflow: "hidden" }}>
            <Box sx={{ p: 2.4, borderBottom: "1px solid rgba(226,232,240,0.9)" }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="34%" height={30} />
                  <Skeleton variant="text" width="18%" height={22} />
                </Box>
                <Skeleton variant="rounded" width={92} height={26} sx={{ borderRadius: 999 }} />
              </Stack>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "minmax(0, 1fr) 280px",
                },
                minHeight: 210,
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {topFields.map((__, fieldIndex) => (
                    <Box
                      key={fieldIndex}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        backgroundColor: "#F8FAFC",
                        border: "1px solid rgba(226,232,240,0.95)",
                      }}
                    >
                      <Skeleton variant="text" width="44%" height={18} />
                      <Skeleton variant="text" width="72%" height={24} sx={{ mt: 0.85 }} />
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    mt: 1.5,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {lowerFields.map((__, fieldIndex) => (
                    <Skeleton key={fieldIndex} variant="rounded" height={70} sx={{ borderRadius: 2.5 }} />
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderTop: { xs: "1px solid rgba(226,232,240,0.9)", xl: "none" },
                  borderLeft: { xl: "1px solid rgba(226,232,240,0.9)" },
                  backgroundColor: "#F8FAFC",
                }}
              >
                <Skeleton variant="text" width="42%" height={18} />
                <Skeleton variant="text" width="68%" height={24} sx={{ mt: 1 }} />
                <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 2, borderRadius: 2 }} />
                <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 1, borderRadius: 2 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Grid>
  );
}

export default function ManufacturingProducts() {
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [products, setProducts] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const activeCount = products.filter((product) =>
    Boolean(product.is_active),
  ).length;
  const linkedAssetCount = products.filter(
    (product) => product.inventory_asset_type?.name,
  ).length;
  const linkedDeviceCount = products.filter(
    (product) => product.device_type?.name,
  ).length;
  //  const [deleting, setDeleting] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [errors, setErrors] = useState({});
  const loadPartners = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getRequest(
        `${MANUFACTURING_API}/products`,
        token,
        activeOrg,
      );
      setProducts(data?.products || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing partners"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  const loadData = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      const [productsData, assetTypesData, deviceTypesData] = await Promise.all(
        [
          getRequest(`${MANUFACTURING_API}/products`, token, activeOrg),
          getRequest(`${INVENTORY_API}/asset-types`, token, activeOrg),
          getRequest(DEVICE_TYPES_API, token, activeOrg),
        ],
      );
      setProducts(
        normalizeCollection(productsData, ["products", "data", "items"]),
      );
      setAssetTypes(
        normalizeCollection(assetTypesData, ["data", "items", "asset_types"]),
      );
      setDeviceTypes(
        normalizeCollection(deviceTypesData, ["data", "items", "device_types"]),
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing products"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    if (!open) {
      setErrors({});
      setForm(emptyForm);
      setEditingId(null);
    }
  }, [open]);
  const handleCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setForm({
      name: product.name || "",
      code: product.code || "",
      inventory_asset_type_id: product.inventory_asset_type_id || "",
      device_type_id: product.device_type_id || "",
      manufacturer_name: product.manufacturer_name || "CoreData",
      model_name: product.model_name || "",
      hardware_revision: product.hardware_revision || "A1",
      bom_revision: product.bom_revision || "",
      pcb_revision: product.pcb_revision || "",
      enclosure_revision: product.enclosure_revision || "",
      default_base_os_version: product.default_base_os_version || "",
      default_test_plan_version: product.default_test_plan_version || "",
      is_active: Boolean(product.is_active),
      notes: product.notes || "",
    });
    setErrors({});
    setOpen(true);
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Required";
    if (!form.code.trim()) newErrors.code = "Required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    setError("");

    try {
      if (editingId) {
        await patchRequest(
          `${MANUFACTURING_API}/products/${editingId}`, // ✅ FIX
          form,
          token,
          activeOrg,
        );
        setSuccess("Manufacturing product updated"); // ✅ FIX
      } else {
        await postRequest(
          `${MANUFACTURING_API}/products`, // ✅ FIX
          form,
          token,
          activeOrg,
        );
        setSuccess("Manufacturing product created"); // ✅ FIX
      }

      setOpen(false);
      await loadData(); // ✅ FIX
    } catch (err) {
      setError(getErrorMessage(err, "Unable to save manufacturing product")); // ✅ FIX
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (product) => {
    setSelectedPartner(product);
    setDeleteOpen(true);
  };
  const confirmDelete = async () => {
    setLoadingDelete(true);
    try {
      await deleteRequest(
        `${MANUFACTURING_API}/products/${selectedPartner.id}`,
        token,
        activeOrg,
      );
      setSuccess("Manufacturing product deleted");
      setDeleteOpen(false);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete manufacturing product"));
    } finally {
      setLoadingDelete(false);
    }
  };
  return (
    <PageShell
      title="Manufacturing Products"
      subtitle="Define manufacturable SKUs and map them to inventory asset types and logical device types."
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                       whiteSpace: "nowrap",
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
        >
          Add Product
        </Button>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={controlDeskSx}>
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", xl: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize:{xs:10,md:15},
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#64748B",
              }}
            >
              Product Definitions
            </Typography>
            <Typography
              sx={{ mt: 0.75, fontSize: 15, color: "#334155", lineHeight: 1.7 }}
            >
              Manufacturing-ready SKUs with inventory mapping, device-type
              alignment, and revision baseline visibility.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ minWidth: { xl: 560 } }}
          >
            {[
              ["Products", products.length],
              ["Active", activeCount],
              ["Asset linked", linkedAssetCount],
              ["Device linked", linkedDeviceCount],
            ].map(([label, value]) => (
              <Box key={label} sx={{ ...metricTileSx, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.65,
                    fontSize: 24,
                    fontWeight: 900,
                    color: "#0F172A",
                    letterSpacing: "-0.04em",
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <ManufacturingProductsSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {products.map((product) => (
            <Box sx={{ width: "100%" }} key={product.id}>
              <SectionCard
                title={product.name}
                subtitle={product.code}
                action={statusChip(product.is_active ? "ACTIVE" : "INACTIVE")}
                contentSx={{
                  p: 0,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      xl: "minmax(0, 1fr) 280px",
                    },
                    minHeight: 210,
                  }}
                >
                  <Box sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 1.25,
                      }}
                    >
                      {[
                        ["Model", product.model_name || "Not set"],
                        ["HW Rev", product.hardware_revision || "Not set"],
                        [
                          "Asset Type",
                          product.inventory_asset_type?.name || "Not linked",
                        ],
                        [
                          "Device Type",
                          product.device_type?.name || "Not linked",
                        ],
                      ].map(([label, value]) => (
                        <Box
                          key={label}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: "#F8FAFC",
                            border: "1px solid rgba(226,232,240,0.95)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 800,
                              color: "#64748B",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.85,
                              fontSize: 15,
                              fontWeight: 800,
                              color: "#0F172A",
                              wordBreak: "break-word",
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box
                      sx={{
                        mt: 1.5,
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.25,
                      }}
                    >
                      {[
                        [
                          "Manufacturer",
                          product.manufacturer_name || "CoreData",
                        ],
                        [
                          "Base OS",
                          product.default_base_os_version || "Not set",
                        ],
                        [
                          "Test Plan",
                          product.default_test_plan_version || "Not set",
                        ],
                      ].map(([label, value]) => (
                        <Box
                          key={label}
                          sx={{
                            p: 1.5,
                            borderRadius: 2.5,
                            backgroundColor: "#F8FAFC",
                            border: "1px solid rgba(226,232,240,0.95)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 800,
                              color: "#64748B",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}
                          >
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.85,
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      p: 2.5,
                      borderTop: {
                        xs: "1px solid rgba(226,232,240,0.9)",
                        xl: "none",
                      },
                      borderLeft: { xl: "1px solid rgba(226,232,240,0.9)" },
                      backgroundColor: "#F8FAFC",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#64748B",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Actions
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: 14,
                        color: "#475569",
                        lineHeight: 1.7,
                      }}
                    >
                      Update the manufacturing definition or delete this product
                      when it is no longer active.
                    </Typography>
                    <Stack
                      direction={{ xs: "column", md: "row", xl: "column" }}
                      spacing={1.2}
                      sx={{ mt: 2 }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleEdit(product)}
                        sx={{ fontWeight: 800, width: { xs: "100%", md: 220 } }}
                      >
                        Edit Product
                      </Button>
                      <Button
                        fullWidth
                        color="error"
                        variant="outlined"
                        onClick={() => handleDelete(product)}
                        sx={{ fontWeight: 800, width: { xs: "100%", md: 220 } }}
                      >
                        Delete Product
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              </SectionCard>
            </Box>
          ))}
          {!products.length ? (
            <Grid item xs={12}>
              <EmptyStateCard
                title="No manufacturing products yet"
                description="Create the first manufacturable product definition before opening production orders."
              />
            </Grid>
          ) : null}
        </Grid>
      )}

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setErrors({});
          setForm(emptyForm);
          setEditingId(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            background:
               "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          color: "#fff",
          boxShadow: "0 28px 60px rgba(15,23,42,0.18)",
          }}
        >
          {editingId
            ? "Edit Manufacturing Product"
            : "Create Manufacturing Product"}
          {/* CLOSE ICON */}
          <IconButton
            onClick={() => {
              setOpen(false);
              setErrors({});
              setForm(emptyForm);
              setEditingId(null);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#F8FAFC",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Product Name"
                  required
                  value={form.name}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, name: e.target.value }));
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  error={!!errors.name}
                  helperText={errors.name || ""}
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Product Code"
                  required
                  value={form.code}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, code: e.target.value }));
                    setErrors((prev) => ({ ...prev, code: "" }));
                  }}
                  error={!!errors.code}
                  helperText={errors.code || ""}
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Inventory Asset Type"
                  value={form.inventory_asset_type_id}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      inventory_asset_type_id: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {assetTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Device Type"
                  value={form.device_type_id}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, device_type_id: e.target.value }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {deviceTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Manufacturer Name"
                  value={form.manufacturer_name}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      manufacturer_name: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <TextField
                  fullWidth
                  label="Model Name"
                  value={form.model_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, model_name: e.target.value }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "23%" } }}>
                <TextField
                  fullWidth
                  label="Hardware Revision"
                  value={form.hardware_revision}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      hardware_revision: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "23%" } }}>
                <TextField
                  fullWidth
                  label="BOM Revision"
                  value={form.bom_revision}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, bom_revision: e.target.value }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "23%" } }}>
                <TextField
                  fullWidth
                  label="PCB Revision"
                  value={form.pcb_revision}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, pcb_revision: e.target.value }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "23%" } }}>
                <TextField
                  fullWidth
                  label="Enclosure Revision"
                  value={form.enclosure_revision}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      enclosure_revision: e.target.value,
                    }))
                  }
                />
              </Box>
              <Box sx={{ width: { xs: "100%", md: "30%" } }}>
                <TextField
                  select
                  fullWidth
                  label="Status*"
                  value={form.is_active}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.value === "true", 
                    }));
                    setErrors((prev) => ({ ...prev, is_active: "" }));
                  }}
                  error={!!errors.is_active}
                  helperText={errors.is_active || ""}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ width: { xs: "100%", md: "30%" } }}>
                <TextField
                  fullWidth
                  label="Default Base OS Version"
                  value={form.default_base_os_version}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      default_base_os_version: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: { xs: "100%", md: "30%" } }}>
                <TextField
                  fullWidth
                  label="Default Test Plan Version"
                  value={form.default_test_plan_version}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      default_test_plan_version: e.target.value,
                    }))
                  }
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Notes"
                  value={form.notes}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpen(false);
              setErrors({});
              setForm(emptyForm);
              setEditingId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              minWidth: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : editingId ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to Delete this? </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>

          <Button
            color="error"
            variant="contained"
            disabled={loadingDelete}
            onClick={confirmDelete}
          >
            {loadingDelete ? <CircularProgress size={16} /> : "YES, DELETE"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
